/// <amd-module name="Build/Admin/Agent/Actions/AgentActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { TaskAgent, TaskAgentPool, TaskAgentPoolOptions, TaskAgentPoolReference, TaskAgentJobRequest } from "DistributedTask/Client/Contracts/TaskAgent";

import { ActionsHub } from "Build/Admin/Agent/Actions/ActionsHub";
import { AgentSource } from "Build/Admin/Agent/Sources/AgentSource";
import { StoresHub } from "Build/Admin/Agent/Stores/StoresHub";
import { ConfirmationDialogActionCreator } from "Build/Admin/Common/Actions/ConfirmationDialogActionCreator";
import { NavigationActionCreator } from "Build/Admin/Common/Actions/NavigationActionCreator";
import { NotificationActionCreator } from "Build/Admin/Common/Actions/NotificationActionCreator";
import { ProjectActionCreator } from "Build/Admin/Common/Actions/ProjectActionCreator";
import { TaskAgentData } from "Build/Admin/Common/Client/Contracts/DistributedTask/DataProvider";
import { AgentHubViewKeys, Telemetry } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { JobSource } from "Build/Admin/Common/Sources/JobSource";
import { NotificationType } from "Build/Admin/Common/Stores/NotificationStore";
import { isElastic } from "Build/Admin/Common/TaskAgentPoolUtils";

const defaultTab: string = AgentHubViewKeys.Jobs;

export interface IAgentActionCreatorOptions {
    actionsHub: ActionsHub;
    agentSource: AgentSource;
    jobSource: JobSource;
    pageContext: IVssPageContext;
    storesHub: StoresHub;
}

export class AgentActionCreator {
    public confirmationDialogActionCreator: ConfirmationDialogActionCreator<string>;
    public navigationActionCreator: NavigationActionCreator;
    public notificationActionCreator: NotificationActionCreator;
    public projectActionCreator: ProjectActionCreator;

    private _actionsHub: ActionsHub;
    private _agentSource: AgentSource;
    private _jobSource: JobSource;
    private _pageContext: IVssPageContext;
    private _storesHub: StoresHub;

    constructor(options: IAgentActionCreatorOptions) {
        this._actionsHub = options.actionsHub;
        this._agentSource = options.agentSource;
        this._jobSource = options.jobSource;
        this._pageContext = options.pageContext;
        this._storesHub = options.storesHub;

        this.confirmationDialogActionCreator = new ConfirmationDialogActionCreator<string>({ confirmationDialogActions: this._actionsHub.confirmationDialogActions });
        this.navigationActionCreator = new NavigationActionCreator({ pageContext: this._pageContext });
        this.notificationActionCreator = new NotificationActionCreator({ notificationActions: this._actionsHub.notificationActions });
        this.projectActionCreator = new ProjectActionCreator({ pageContext: options.pageContext, projectActions: this._actionsHub.projectActions });
    }

    public dispose(): void {
        this.projectActionCreator.dispose();
    }

    public initializeAgentData(): void {
        const agentData: TaskAgentData | undefined = this._agentSource.getCachedAgentData();
        let tabIdParam: string | undefined = this.navigationActionCreator.getTabIdParam();

        if (agentData && this._validateAgentData(agentData)) {
            this._actionsHub.selectedAgentUpdated.invoke(agentData.selectedAgent);
            this._actionsHub.selectedAgentQueueUpdated.invoke(agentData.selectedAgentQueue);
            this._actionsHub.selectedAgentPoolUpdated.invoke(agentData.selectedAgentPool);

            if (tabIdParam && this._validateTabIdParam(tabIdParam, agentData.selectedAgentPool)) {
                this._actionsHub.selectedTabUpdated.invoke(tabIdParam);
            }
            else {
                tabIdParam = defaultTab;
                this._actionsHub.selectedTabUpdated.invoke(defaultTab);
            }

            const queueId: number | undefined = agentData.selectedAgentQueue ? agentData.selectedAgentQueue.id : undefined;
            const poolId: number | undefined = agentData.selectedAgentPool ? agentData.selectedAgentPool.id : undefined;
            this.navigationActionCreator.replaceQueryParameters(queueId, poolId, tabIdParam, agentData.selectedAgent.id);

            this.projectActionCreator.initializeProjects();

            const jobs: TaskAgentJobRequest[] = this._jobSource.getCachedJobs();
            this._actionsHub.jobActions.jobsUpdated.invoke(jobs);
        }
        else {
            // If the data for the view is bad, step out to the next best view
            const linkingService: IPoolAdminLinkingService = this._pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            const projectDataService: IProjectDataService = this._pageContext.getService<IProjectDataService>("IProjectDataService");
            if (agentData && agentData.selectedAgentQueue) {
                const poolLink: string = projectDataService.hasProjectData()
                    ? linkingService.getAgentQueueUrl(agentData.selectedAgentQueue)
                    : linkingService.getAgentPoolUrl(agentData.selectedAgentPool);
                FastPageSwitch(this._pageContext, poolLink, false);
            }
            else {
                const poolListLink: string = projectDataService.hasProjectData()
                    ? linkingService.getProjectPoolsUrl()
                    : linkingService.getAccountPoolsUrl();
                FastPageSwitch(this._pageContext, poolListLink, false);
            }
        }
    }

    public updateSelectedTab(newTabId: string): void {
        if (this._validateTabIdParam(newTabId, this._storesHub.selectionStore.state.selectedAgentPool) &&
            newTabId !== this._storesHub.selectionStore.state.selectedTabId) {
            this.navigationActionCreator.setTabIdParam(newTabId);
            this._actionsHub.selectedTabUpdated.invoke(newTabId);
        }
    }

    public showAddUserCapabilityPanel(): void {
        this._actionsHub.showAddUserCapabilityPanel.invoke(undefined);
    }

    public dismissAddUserCapabilityPanel(): void {
        this._actionsHub.dismissAddUserCapabilityPanel.invoke(undefined);
    }

    public addUserCapability(pool: TaskAgentPoolReference, agent: TaskAgent, name: string, value: string): void {
        const userCapabilities: { [key: string]: string } = { ...agent.userCapabilities };
        userCapabilities[name] = value;
        this._updateUserCapabilitiesHelper(pool, agent, userCapabilities);
    }

    public deleteUserCapability(pool: TaskAgentPoolReference, agent: TaskAgent, name: string): void {
        const userCapabilities: { [key: string]: string } = { ...agent.userCapabilities };
        delete userCapabilities[name];
        this._updateUserCapabilitiesHelper(pool, agent, userCapabilities);
    }

    public getRequestData(poolId: number, agentId: number, requestId: number): void {
        if (this._storesHub.selectionStore.state.selectedAgent &&
            this._storesHub.selectionStore.state.selectedAgent.id === agentId) {
            this._jobSource.getAgentRequest(poolId, requestId, true).then(
                (agentRequest) => {
                    this._actionsHub.jobActions.jobUpdated.invoke(agentRequest);
                },
                (reason) => {
                    this._handleError(reason);
                }
            );
        }
    }

    private _updateUserCapabilitiesHelper(
        selectedAgentPool: TaskAgentPoolReference,
        selectedAgent: TaskAgent,
        userCapabilities: { [key: string]: string }): void {

        const AgentToUpdate: TaskAgent = { ...selectedAgent, userCapabilities };

        // Update the agent locally so UI updates
        this._actionsHub.selectedAgentUpdated.invoke(AgentToUpdate);

        this._agentSource.updateAgentUserCapabilities(selectedAgentPool.id, selectedAgent.id, userCapabilities).then(
            (updatedAgent: TaskAgent) => {
                // Go ahead and update again, in case there's anything else new on the agent object
                this._actionsHub.selectedAgentUpdated.invoke(updatedAgent);
            },
            (reason) => {
                // "Roll back" the update to the store
                this._actionsHub.selectedAgentUpdated.invoke(selectedAgent);
                this._handleError(reason);
            });
    }

    private _validateAgentData(agentData: TaskAgentData | undefined): boolean {
        if (agentData &&
            agentData.selectedAgent &&
            (agentData.selectedAgentQueue || agentData.selectedAgentPool)) {
            return true;
        }
        else {
            return false;
        }
    }

    private _validateTabIdParam(tabId: string, taskAgentPool: TaskAgentPool | undefined): boolean {
        if (tabId === AgentHubViewKeys.Capabilities) {
            // Elastic Agent Pools do not support capabilities
            if (isElastic(taskAgentPool)) {
                return false;
            }

            return true;
        }
        else if (tabId === AgentHubViewKeys.Jobs) {
            return true;
        }
        else {
            return false;
        }
    }

    // tslint:disable-next-line:no-any
    private _handleError(reason: any): void {
        if (reason && reason.message) {
            this.notificationActionCreator.addNotification(reason.message, NotificationType.error);
            const service = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            service.publishEvent(Telemetry.Area, Telemetry.PoolsAdminFeature, {
                message: reason.message,
                error: reason
            });
        }
    }
}
